import React, { useState, useEffect, useCallback } from 'react';
import ProgressMessage from 'components/Utils/ProgressMessage';
import Icon from 'components/Utils/NewIcon';
import DocumentPreview from 'components/Utils/DocumentPreview';
import './style.scss';
import DocumentUtils from 'services/DocumentUtils';
import MiscUtils from 'services/MiscUtils';
import { DispatchOptions } from 'careand-redux/useAppDispatch';

interface LargeResourcePreviewProps {
  url?: string;
  originalUrl?: string;
  type?: string;
  autoLoad?: boolean;
  fitWidth?: boolean;
  getFile?: <T extends DispatchOptions>(options: T) => Promise<Blob | undefined> | any;
  getFileInfo?: () => Promise<{ url: string; type?: string } | null | undefined>;
  onLoad?: (url: string, type: string) => void;
  locked?: boolean;
  unlockDateText?: string;
}

const LargeResourcePreview: React.FC<LargeResourcePreviewProps> = ({
  url: initialUrl,
  originalUrl: initialOriginalUrl,
  type: initialType,
  autoLoad,
  fitWidth,
  getFile,
  getFileInfo,
  onLoad,
  locked,
  unlockDateText,
}) => {
  const [url, setUrl] = useState<string | undefined>(initialUrl);
  const [originalUrl, setOriginalUrl] = useState<string | undefined>(initialOriginalUrl);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [type, setType] = useState<string | undefined>(initialType);

  const previewFile = useCallback(async () => {
    if (locked) return;
    if (loading || url) return;

    setLoading(true);
    const {
      fileUrl,
      type: fileType,
      originalUrl: fetchedOriginalUrl,
    } = await DocumentUtils.getFileUrl(getFile!, getFileInfo);
    if (fileUrl) {
      setOriginalUrl(fetchedOriginalUrl);
      setType(fileType);
      setUrl(fileUrl);
      setLoading(false);
      if (onLoad) {
        onLoad(fileUrl, fileType);
      }
    } else {
      MiscUtils.showToastrError(
        'Load Document',
        'Failed to load document. It may have been deleted by another user.',
      );
      setLoadFailed(true);
    }
  }, [getFile, getFileInfo, loading, onLoad, url, locked]);

  useEffect(() => {
    if (!url && !!autoLoad && !loadFailed && !locked) {
      previewFile();
    }
  }, [url, autoLoad, previewFile, loadFailed, locked]);

  if (locked) {
    return (
      <div className="large-resource-preview">
        <DocumentPreview locked unlockDateText={unlockDateText} />
      </div>
    );
  }

  return (
    <div className="large-resource-preview clickable" onClick={previewFile}>
      {(loading === true || url === undefined) && (
        <div className="preview-icon">
          {loading ? (
            <ProgressMessage />
          ) : (
            <>
              <Icon src="file-pdf" alt={url} />
              <span>Click to load</span>
            </>
          )}
        </div>
      )}
      {url !== undefined && (
        <DocumentPreview url={url} originalUrl={originalUrl} type={type} fitWidth={fitWidth} />
      )}
    </div>
  );
};

export default LargeResourcePreview;
